import { formatDistanceToNow } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { CronJobRow } from '@/types';
import { ForceRunButton } from './force-run-button';
import { JobToggle } from './job-toggle';

interface CronJobsCardProps {
  title: string;
  description: string;
  jobs: CronJobRow[];
  eventSlug?: string | null;
  emptyMessage?: string;
}

// `lastRun`/`nextRun`/`enabled` are merged into each row by CronJobs.rows on the server
// (PORTING.md C6), so this card only formats what it was handed.
export function CronJobsCard({
  title,
  description,
  jobs,
  eventSlug,
  emptyMessage = 'No jobs in this group',
}: CronJobsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title} ({jobs.length})
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job Name</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead>Next Run</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow key={job.name}>
                  <TableCell className="font-medium">{job.name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {job.schedule ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    <div
                      className="max-w-[20rem] truncate"
                      title={job.description ?? 'No description'}
                    >
                      {job.description ?? 'No description'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {job.lastRun
                      ? formatDistanceToNow(new Date(job.lastRun), {
                          addSuffix: true,
                        })
                      : 'Never'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {job.nextRun
                      ? formatDistanceToNow(new Date(job.nextRun), {
                          addSuffix: true,
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {job.oneOff ? (
                      <span className="text-muted-foreground text-sm">
                        One-off
                      </span>
                    ) : (
                      <JobToggle
                        jobName={job.name}
                        enabled={job.enabled}
                        eventSlug={eventSlug}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <ForceRunButton
                      jobName={job.name}
                      isOneOff={job.oneOff}
                      description={job.description}
                      returnsDownload={job.returnsDownload}
                      acceptsCsvUpload={job.acceptsCsvUpload}
                      acceptsJsonUpload={job.acceptsJsonUpload}
                      eventSlug={eventSlug}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
